import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Pause, Play, RotateCcw } from "lucide-react";

const clamp = (v) => Math.min(1, Math.max(0, v));

export const VideoReveal = ({ src, start = 0, stages, testId, alt }) => {
  const wrap = useRef(null);
  const vid = useRef(null);
  const track = useRef(null);
  const dragging = useRef(false);
  const [p, setP] = useState(0);
  const [playing, setPlaying] = useState(false);
  const inView = useInView(wrap, { margin: "-20% 0px" });

  const span = () => Math.max(0.1, (vid.current?.duration || 1) - start);

  const play = () => {
    const v = vid.current;
    if (!v) return;
    if (v.ended || v.currentTime >= v.duration - 0.05 || v.currentTime < start) v.currentTime = start;
    v.play().catch(() => {});
  };

  const seek = (q) => {
    const v = vid.current;
    v.pause();
    v.currentTime = start + clamp(q) * span();
    setP(clamp(q));
  };

  const seekX = (clientX) => {
    const r = track.current.getBoundingClientRect();
    seek((clientX - r.left) / r.width);
  };

  useEffect(() => {
    if (inView) play();
    else vid.current?.pause();
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let id;
    const tick = () => {
      const v = vid.current;
      if (v && v.duration && !dragging.current && !v.paused) setP(clamp((v.currentTime - start) / span()));
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [start]); // eslint-disable-line react-hooks/exhaustive-deps

  const stage = stages[Math.min(stages.length - 1, Math.floor(p * stages.length))];
  const done = p > 0.985 && !playing;

  return (
    <div ref={wrap} data-testid={testId} className="corner-ticks relative aspect-[16/9] select-none overflow-hidden bg-[#121212]">
      <video
        ref={vid}
        poster={src.poster}
        muted
        loop
        playsInline
        preload="auto"
        aria-label={alt}
        onLoadedMetadata={(e) => { e.currentTarget.currentTime = start; }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={() => (playing ? vid.current.pause() : play())}
        style={{ filter: `grayscale(${1 - p})` }}
        className="absolute inset-0 h-full w-full cursor-pointer object-cover"
      >
        <source src={src.webm} type="video/webm" />
        <source src={src.mp4} type="video/mp4" />
      </video>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0A0A]/90 to-transparent" />
      <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-full bg-[#0A0A0A]/70 px-3 py-1.5 backdrop-blur-md">
        <span className={`h-1.5 w-1.5 rounded-full ${p > 0.97 ? "bg-emerald-400" : "bg-white"}`} />
        <span data-testid={`${testId}-stage`} className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-100">{stage}</span>
      </div>

      <div className="absolute inset-x-4 bottom-4 flex items-center gap-4 md:inset-x-6 md:bottom-6">
        <button
          type="button"
          data-testid={`${testId}-play`}
          aria-label={playing ? "Pause" : done ? "Replay" : "Play"}
          onClick={() => (playing ? vid.current.pause() : play())}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#0A0A0A] transition-transform duration-300 hover:scale-105"
        >
          {playing ? <Pause className="h-4 w-4" /> : done ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
        </button>
        <span className="hidden font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-300 sm:block">Before</span>
        <div
          ref={track}
          role="slider"
          tabIndex={0}
          aria-label="Scrub from before to after"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(p * 100)}
          data-testid={`${testId}-scrubber`}
          data-cursor="Scrub"
          onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); seekX(e.clientX); }}
          onPointerMove={(e) => dragging.current && seekX(e.clientX)}
          onPointerUp={() => { dragging.current = false; play(); }}
          onPointerCancel={() => { dragging.current = false; }}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") seek(p - 0.05);
            if (e.key === "ArrowRight") seek(p + 0.05);
          }}
          className="group relative h-8 flex-1 cursor-ew-resize touch-none focus:outline-none"
        >
          <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 bg-white/20" />
          <div className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 bg-white" style={{ width: `${p * 100}%` }} />
          {stages.map((s, i) => i > 0 && (
            <span key={s} className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-white/40" style={{ left: `${(i / stages.length) * 100}%` }} />
          ))}
          <span className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#0A0A0A] bg-white shadow-[0_0_0_6px_rgba(255,255,255,0.15)] transition-transform group-hover:scale-125 group-focus-visible:ring-2 group-focus-visible:ring-white" style={{ left: `${p * 100}%` }} />
        </div>
        <span className="hidden font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white sm:block">After</span>
      </div>
    </div>
  );
};
